// `useConvList` — Wave 5c.1.
//
// Wraps `useConversations()` (infinite query) + flattens the pages so the
// DS-D `ConvList` composite can render rows directly. Sort is most-recent
// first (US-066). Filter shape mirrors the API contract:
//
//   - `accountId`: 'all' | linkedinAccountId
//   - `mode`:      'all' | 'action' | 'auto' | 'review' | 'offtarget'
//
// Pagination: callers can `fetchNextPage()` (e.g. via `IntersectionObserver`
// or a "Load more" button — we surface both helpers).
//
// Realtime: see `InboxPage.tsx` — the `message.in` listener invalidates
// `['conversations']` so the next reconciliation re-orders.

import {
  useConversations,
  type ApiError,
  type ConversationListItem,
  type ConversationsListFilter,
} from '@barel/api-client/react';
import { useMemo } from 'react';

export interface UseConvListReturn {
  /** Flattened list of conversations across all loaded pages. */
  items: ConversationListItem[];
  isLoading: boolean;
  isError: boolean;
  error: ApiError | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

export function useConvList(filter: ConversationsListFilter = {}): UseConvListReturn {
  const query = useConversations(filter);

  const items = useMemo(() => {
    const all: ConversationListItem[] = [];
    const seen = new Set<string>();
    for (const page of query.data?.pages ?? []) {
      for (const conv of page.conversations) {
        if (seen.has(conv.id)) continue;
        seen.add(conv.id);
        all.push(conv);
      }
    }
    // Server already returns most-recent first; pagination preserves order.
    // We additionally sort defensively in case a realtime patch perturbed the
    // page boundary. Pending-action rows bubble to the top of their account
    // group (US-066).
    return all.slice().sort((a, b) => {
      const aPending = a.pendingActionType ? 1 : 0;
      const bPending = b.pendingActionType ? 1 : 0;
      if (aPending !== bPending) return bPending - aPending;
      const aTs = a.lastMessageAt ?? 0;
      const bTs = b.lastMessageAt ?? 0;
      if (aTs !== bTs) return bTs - aTs;
      return a.id.localeCompare(b.id);
    });
  }, [query.data]);

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
    refetch: () => {
      void query.refetch();
    },
  };
}
